import { get } from 'lodash'
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import qs from 'query-string'
import { CellProps } from 'react-table'

import { filterByRequireRules } from 'civic-champs-shared/utils/filterByRequireRules'
import { useUISettings } from 'hooks/useUISettings'
import { EditCell, EditHeader } from 'core/table/cells'
import { booleanSortType } from 'core/table/sort'
import { RoleWithReporting } from '../types'

export const useStyles = makeStyles({
  link: {
    color: '#0F5DB5',
    textDecoration: 'none',
    cursor: 'pointer',
  },
  selectColumn: {
    '&>div': {
      width: '46px',
    },
  },
  idColumn: {
    '&>div': {
      width: '70px',
    },
  },
  nameColumn: {
    '&>div': {
      width: '250px',
    },
  },
  totalActivitiesColumn: {
    '&>div': {
      width: '100px',
    },
  },
  statusColumn: {
    '&>div': {
      width: '150px',
    },
  },
  typeColumn: {
    '&>div': {
      width: '150px',
    },
  },
  actionsColumn: {
    '&>div': {
      width: '80px',
    },
  },
})

export const RoleNameCell = ({ cell }: any) => {
  const { value } = cell
  const classes = useStyles()
  return (
    <Link
      className={classes.link}
      to={{
        pathname: `/activities/`,
        search: qs.stringify({ 'role:anyOf': [value] }, { arrayFormat: 'bracket' }),
      }}
    >
      {value}
    </Link>
  )
}

export function useRolesColumns({
  editRole,
  deleteRole,
}: {
  editRole: (role: { id: number; name: string }) => void
  deleteRole: (role: { id: number; name: string }) => void
}) {
  const classes = useStyles()
  // @ts-ignore
  const { showAdminItems } = useUISettings()

  return useMemo(
    () =>
      filterByRequireRules(
        [
          {
            id: 'id',
            Header: 'Id',
            accessor: 'id',
            disableFilters: true,
            requires: 'showAdminItems',
          },
          {
            id: 'order',
            Header: 'Order',
            Cell: ({ row }: any) => row.index + 1,
            disableFilters: true,
            disableSortBy: true,
          },
          {
            id: 'name',
            Header: 'Name',
            accessor: 'name',
            Cell: RoleNameCell,
            disableFilters: true,
            className: classes.nameColumn,
          },
          {
            id: 'totalActivities',
            Header: 'Total Activities',
            accessor: 'totalActivities',
            disableFilters: true,
            className: classes.totalActivities,
          },
          {
            id: 'status',
            Header: 'Status',
            accessor: 'isActive',
            Cell: ({ cell }: CellProps<RoleWithReporting>) => {
              const { isActive } = cell.row.original
              return isActive ? 'Active' : 'Inactive'
            },
            className: classes.statusColumn,
            sortType: booleanSortType<RoleWithReporting>(),
          },
          {
            id: 'type',
            Header: 'Type',
            accessor: 'isDefault',
            Cell: ({ cell }: CellProps<RoleWithReporting>) => {
              const { isDefault } = cell.row.original
              return isDefault ? 'Opportunity' : 'Event'
            },
            className: classes.typeColumn,
            sortType: booleanSortType<RoleWithReporting>({ invert: true }),
          },
          {
            id: 'actions',
            Header: <EditHeader hideDelete={!showAdminItems} />,
            Cell: ({ cell }: CellProps<RoleWithReporting>) => (
              <EditCell cell={cell} onEdit={editRole} onDelete={showAdminItems ? deleteRole : undefined} />
            ),
            disableSortBy: true,
            disableFilters: true,
            className: classes.actionsColumn,
          },
        ],
        {
          showAdminItems,
        },
      ),
    [
      classes.nameColumn,
      classes.totalActivities,
      classes.statusColumn,
      classes.typeColumn,
      classes.actionsColumn,
      showAdminItems,
      editRole,
      deleteRole,
    ],
  )
}

export default useRolesColumns
